import React, { type PropsWithChildren, useEffect } from 'react';
import { FormProvider as BaseFormProvider, type FieldValues, type UseFormReturn } from 'react-hook-form';
import styled from 'styled-components';

import { useFormErrorHandler } from '../../hooks/forms/useFormErrorHandler';

import type { ApiErrorResponse, RootError } from '@capsicum/common';
import type { AxiosError } from 'axios';

type FormProviderParamsType<FormShape extends FieldValues> = {
	error?: AxiosError<ApiErrorResponse> | null;
	form: UseFormReturn<FormShape, unknown, FormShape>;
	onRootError?: (errors: RootError) => void;
	testID: string;
} & React.HTMLAttributes<HTMLDivElement>;

export const FormProvider = <FormShape extends FieldValues>({
	children,
	error,
	form,
	onRootError,
	testID,
	...props
}: PropsWithChildren<FormProviderParamsType<FormShape>>) => {
	const { formState } = form;
	const { onError } = useFormErrorHandler<FormShape>(form);

	useEffect(() => {
		if (error) onError?.(error);
	}, [error]);

	useEffect(() => {
		if (formState.errors.root) {
			onRootError?.(formState.errors.root);
		}
	}, [formState.errors.root]);

	return (
		<S.FormWrapper id={`${testID}-form`} {...props}>
			<BaseFormProvider {...form}>{children}</BaseFormProvider>
		</S.FormWrapper>
	);
};

namespace S {
	export const FormWrapper = styled.div`
		width: 100%;
	`;
}
